<script setup>
import { ref, watch } from 'vue';
import InfoTooltip from './pge-info-tooltip.vue';

const props = defineProps({
  label: String,
  model: {
    type: String,
    required: true
  },
  minLength: {
    type: Number,
    default: 1,
  },
  resize: {
    type: String,
    default: 'resize-none'
  },
  errorMessage: {
    type: String,
    default: 'Cannot be blank'
  },
  rows: {
    type: Number,
    default: 4
  },
  tooltip: {
    type: String,
    default: ''
  },
  name: String,
  placeholder: String,
  maxLength: Number,
  backgroundColor: String,
  info: String,
  disabled: Boolean,
  readonly: Boolean,
  focused: Boolean,
  required: Boolean,
  hasRequiredSign: Boolean,
  shouldShowError: Boolean,
});

const emit = defineEmits(['input', 'change']);
const inputEl = ref(null);
const localModel = ref(props.model || '');
const isFocused = ref(props.focused);

const handleInput = (e) => {
  emit('input');
  emit('update:modelValue', e.target.value);
};

const handleFocus = () => {
  isFocused.value = true;
  emit('update:focused', true);
};

const handleBlur = () => {
  isFocused.value = false;
  emit('update:focused', false);
};

const handleChange = (e) => {
  emit('change', e);
};

watch(() => props.focused, (newValue) => {
  isFocused.value = newValue;
  if (isFocused.value) {
    inputEl.value.focus();
  }
});

watch(() => props.model, (newValue) => {
  localModel.value = newValue;
});
</script>

<template>
  <div class="t-container" :style="{ rowGap: !tooltip ? '8px' : 0 }">
    <label
      :class="disabled ? 'disabled' : undefined"
    >
      <span
        v-if="label"
        :class="[
          isFocused ? 'focused' : undefined,
          required && localModel?.length < minLength && isFocused && shouldShowError ? 'error' : undefined
        ]"
        :style="{ backgroundColor: backgroundColor ? backgroundColor : 'inherit' }"
      >
        {{ label }}
        <span v-if="required && hasRequiredSign">*</span>
      </span>
  
      <textarea 
        ref="inputEl"
        v-model="localModel"
        :name="name"
        :placeholder="placeholder"
        :maxlength="maxLength"
        :disabled="disabled"
        :readonly="readonly"
        :rows="rows"
        :required="required"
        :class="[required && localModel?.length < minLength && isFocused && shouldShowError ? 'error' : undefined, resize]"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @change="handleChange"
      />
    </label>

    <div class="t-container__description">
      <div class="t-container__description__info">
        <div class="t-container__description__info__inner">
          <span
            v-if="required && isFocused && localModel?.length < minLength && shouldShowError"
            class="error"
          >
            {{ errorMessage }}
          </span>
          <span
            v-if="maxLength && isFocused"
            :class="required && localModel?.length < minLength && shouldShowError ? 'error' : undefined"
          >
            {{ localModel?.length }} / {{ maxLength }} Characters Maximum
          </span>
    
          <span v-else-if="maxLength && !isFocused">{{ maxLength }} Characters Maximum</span>
          <span v-else-if="info">{{ info }}</span>
        </div>

        <info-tooltip v-if="tooltip" :tooltip="tooltip" />
      </div>
    </div>
  </div>
</template>

<style scoped>
@import url("./styles/pge-textarea/index.scss");
</style>
